import { useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { emailRegex, strongPasswordRegex } from "../../utils/authUtils";

export default function PasswordResetForm({
  passwordResetData,
  setPasswordResetData,
  passwordResetStep,
  //setPasswordResetStep,
  emailValid,
  setEmailvalid,
  passwordValid,
  setPasswordValid,
  confirmPassword,
  setConfirmPassword,
  showPassword,
  setShowPassword,
  showConfirmPassword,
  setShowConfirmPassword,
  loading,
  passwordResetError,
  handlePasswordResetCode,
  handlePasswordResetSubmit,
  setPasswordReset,
}) {
  useEffect(() => {
    if (passwordResetData.email.trim() === "") {
      setEmailvalid((prev) => ({ ...prev, resetPassword: true }));
    } else if (!emailRegex.test(passwordResetData.email)) {
      setEmailvalid((prev) => ({ ...prev, resetPassword: false }));
    } else {
      setEmailvalid((prev) => ({ ...prev, resetPassword: true }));
    }
  }, [passwordResetData.email, setEmailvalid]);

  useEffect(() => {
    if (passwordResetData.password.trim() === "") {
      setPasswordValid((prev) => ({ ...prev, resetPassword: true }));
    } else if (!strongPasswordRegex.test(passwordResetData.password)) {
      setPasswordValid((prev) => ({ ...prev, resetPassword: false }));
    } else {
      setPasswordValid((prev) => ({ ...prev, resetPassword: true }));
    }
  }, [passwordResetData.password, setPasswordValid]);

  useEffect(() => {
    if (passwordResetData.confirmPassword.trim() === "") {
      setConfirmPassword(true);
    } else if (passwordResetData.password !== passwordResetData.confirmPassword) {
      setConfirmPassword(false);
    } else {
      setConfirmPassword(true);
    }
  }, [passwordResetData.confirmPassword, passwordResetData.password, setConfirmPassword]);

  return (
    <Box component="form" sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" fontWeight="bold" color="primary">
          Password Reset
        </Typography>
        <Button variant="text" onClick={() => setPasswordReset(false)}>
          &lt; Back to login
        </Button>
      </Box>

      {passwordResetError && <Alert severity="error">{passwordResetError}</Alert>}

      {passwordResetStep === 0 && (
        <>
          <TextField
            label="Email Address"
            fullWidth
            margin="normal"
            type="email"
            slotProps={{ htmlInput: { maxLength: 100 } }}
            value={passwordResetData.email}
            onChange={(e) =>
              setPasswordResetData({ ...passwordResetData, email: e.target.value })
            }
            error={!emailValid.resetPassword}
            helperText={!emailValid.resetPassword && "Enter a valid email address"}
          />
          <Button
            fullWidth
            loading={loading}
            loadingPosition="end"
            variant="contained"
            onClick={handlePasswordResetCode}
            disabled={!emailValid.resetPassword || !passwordResetData.email || loading}
            sx={{ mt: 2, py: 1, textTransform: "none" }}
          >
            Verify Email ID
          </Button>
        </>
      )}

      {passwordResetStep === 1 && (
        <>
          <Typography sx={{ color: "gray" }}>
            Enter verification code sent to {passwordResetData.email}
          </Typography>
          <TextField
            label="Verification Code"
            fullWidth
            margin="normal"
            type="text"
            value={passwordResetData.resetCode}
            onChange={(e) =>
              setPasswordResetData({ ...passwordResetData, resetCode: e.target.value })
            }
            slotProps={{ htmlInput: { maxLength: 6 } }}
          />
          <TextField
            label="New Password"
            fullWidth
            margin="normal"
            type={showPassword ? "text" : "password"}
            value={passwordResetData.password}
            onChange={(e) =>
              setPasswordResetData({ ...passwordResetData, password: e.target.value })
            }
            error={!passwordValid.resetPassword}
            helperText={
              !passwordValid.resetPassword &&
              "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character."
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" aria-label="Toggle password visibility">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            slotProps={{ htmlInput: { maxLength: 14 } }}
          />
          <TextField
            label="Confirm Password"
            fullWidth
            margin="normal"
            type={showConfirmPassword ? "text" : "password"}
            value={passwordResetData.confirmPassword}
            onChange={(e) =>
              setPasswordResetData({ ...passwordResetData, confirmPassword: e.target.value })
            }
            error={!confirmPassword}
            helperText={!confirmPassword && "Confirm password mismatch"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    aria-label="Toggle confirm password visibility"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            slotProps={{ htmlInput: { maxLength: 14 } }}
          />
          <Button
            fullWidth
            loading={loading}
            loadingPosition="end"
            variant="contained"
            onClick={handlePasswordResetSubmit}
            disabled={
              !confirmPassword ||
              !passwordValid.resetPassword ||
              !passwordResetData.resetCode ||
              !passwordResetData.password ||
              loading
            }
            sx={{ mt: 2, py: 1, textTransform: "none" }}
          >
            Reset Password
          </Button>
        </>
      )}
    </Box>
  );
}
